import React, { useState, useEffect } from 'react';
import {
  Users,
  HardHat,
  Plus,
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  Clock,
  Search,
  Filter,
  Shield,
  Briefcase,
  AlertCircle,
  ExternalLink,
  Edit2,
  UserX,
  UserCheck,
  Smartphone,
  IdCard,
  Calendar,
  Layers,
  Sparkles,
  ChevronDown,
  ChevronRight,
  Star,
  Check,
  Lock,
  Award,
  AlertTriangle,
} from 'lucide-react';
import {
  StaffItem,
  StaffType,
  StaffAvailability,
  HERE4U_WORK_TYPES,
  Here4uWorkType,
  SkillItem,
  EmployeeSkillItem,
  SkillProficiency,
  SkillVerificationStatus,
} from '../types/index.ts';

interface StaffTeamsPageProps {
  onNavigate: (route: string, param?: string) => void;
}

export interface FormSkillAssignment {
  skillId: number;
  skillName: string;
  category: string;
  isPrimary: boolean;
  proficiency: SkillProficiency;
  qualificationRef: string;
  notes: string;
  verificationStatus: SkillVerificationStatus;
}

export const StaffTeamsPage: React.FC<StaffTeamsPageProps> = ({ onNavigate }) => {
  const [staff, setStaff] = useState<StaffItem[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [skillsCatalogue, setSkillsCatalogue] = useState<SkillItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewTab, setViewTab] = useState<'STAFF' | 'TEAMS'>('STAFF');
  const [availabilityFilter, setAvailabilityFilter] = useState<string>('ALL');
  const [staffTypeFilter, setStaffTypeFilter] = useState<string>('ALL');
  const [workTypeFilter, setWorkTypeFilter] = useState<string>('ALL');
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState<StaffItem | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationSuccessModal, setRegistrationSuccessModal] = useState<StaffItem | null>(null);

  // Universal Skill Catalogue Picker state inside modal
  const [selectedSkills, setSelectedSkills] = useState<FormSkillAssignment[]>([]);
  const [skillsSearch, setSkillsSearch] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    'HVAC': true,
    'Electrical': true,
    'Plumbing': true,
    'Civil and Finishing': true,
    'Doors and Glass': true,
    'Mechanical': true,
    'Security Systems': true,
    'Office Skills': true,
  });

  // Form State for Add / Edit
  const [form, setForm] = useState<{
    employeeId: string;
    name: string;
    mobile: string;
    cnic: string;
    email: string;
    role: string;
    staffType: StaffType;
    designation: string;
    skill: string;
    workTypes: string[];
    teamName: string;
    supervisorId: string;
    availability: StaffAvailability;
    joiningDate: string;
    profilePhoto: string;
    notes: string;
    isActive: boolean;
  }>({
    employeeId: '',
    name: '',
    mobile: '+92 3',
    cnic: '42101-',
    email: '',
    role: 'STAFF',
    staffType: 'TECHNICIAN',
    designation: 'Site Specialist',
    skill: 'Air Conditioning',
    workTypes: ['Air Conditioning'],
    teamName: 'South Rapid Response',
    supervisorId: '',
    availability: 'AVAILABLE',
    joiningDate: new Date().toISOString().split('T')[0],
    profilePhoto: '',
    notes: '',
    isActive: true,
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [staffRes, teamsRes, skillsRes] = await Promise.all([
        fetch('/api/staff').then((r) => (r.ok ? r.json() : [])),
        fetch('/api/teams').then((r) => (r.ok ? r.json() : [])),
        fetch('/api/skills').then((r) => (r.ok ? r.json() : [])),
      ]);

      if (Array.isArray(staffRes)) setStaff(staffRes);
      if (Array.isArray(teamsRes)) setTeams(teamsRes);
      if (Array.isArray(skillsRes)) setSkillsCatalogue(skillsRes);
    } catch (err: any) {
      console.warn('Staff & Teams load notice:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openAddModal = () => {
    setEditingStaff(null);
    setSkillsSearch('');
    // Initial default skill for new technician registration
    const defaultSkill = skillsCatalogue.find((s) => s.name === 'Air Conditioning') || skillsCatalogue[0];
    if (defaultSkill) {
      setSelectedSkills([
        {
          skillId: defaultSkill.id,
          skillName: defaultSkill.name,
          category: defaultSkill.category,
          isPrimary: true,
          proficiency: 'Intermediate',
          qualificationRef: '',
          notes: '',
          verificationStatus: 'UNVERIFIED',
        },
      ]);
    } else {
      setSelectedSkills([]);
    }

    setForm({
      employeeId: 'Auto-assigned by server',
      name: '',
      mobile: '+92 3',
      cnic: '42101-',
      email: '',
      role: 'STAFF',
      staffType: 'TECHNICIAN',
      designation: 'Site Specialist',
      skill: defaultSkill ? defaultSkill.name : 'Air Conditioning',
      workTypes: defaultSkill ? [defaultSkill.name] : ['Air Conditioning'],
      teamName: 'South Rapid Response',
      supervisorId: staff.find((s) => s.staffType === 'SUPERVISOR')?.id ? String(staff.find((s) => s.staffType === 'SUPERVISOR')!.id) : '',
      availability: 'AVAILABLE',
      joiningDate: new Date().toISOString().split('T')[0],
      profilePhoto: '',
      notes: '',
      isActive: true,
    });
    setShowAddModal(true);
  };

  const openEditModal = (member: StaffItem) => {
    setEditingStaff(member);
    setSkillsSearch('');
    if (Array.isArray(member.assignedSkills) && member.assignedSkills.length > 0) {
      setSelectedSkills(
        member.assignedSkills.map((as) => ({
          skillId: as.skillId,
          skillName: as.skillName || 'Skill',
          category: as.category || 'General',
          isPrimary: Boolean(as.isPrimary),
          proficiency: as.proficiency || 'Intermediate',
          qualificationRef: as.qualificationRef || '',
          notes: as.notes || '',
          verificationStatus: as.verificationStatus || 'UNVERIFIED',
        }))
      );
    } else {
      // Find matching skills by name in catalogue if not already populated
      const matching = skillsCatalogue.filter((sk) =>
        (Array.isArray(member.workTypes) && member.workTypes.includes(sk.name)) || member.skill === sk.name
      );
      if (matching.length > 0) {
        setSelectedSkills(
          matching.map((m, idx) => ({
            skillId: m.id,
            skillName: m.name,
            category: m.category,
            isPrimary: idx === 0,
            proficiency: 'Intermediate',
            qualificationRef: '',
            notes: '',
            verificationStatus: 'VERIFIED',
          }))
        );
      } else {
        setSelectedSkills([]);
      }
    }

    setForm({
      employeeId: member.employeeId,
      name: member.name,
      mobile: member.mobile,
      cnic: member.cnic || '',
      email: member.email || '',
      role: member.role || 'STAFF',
      staffType: member.staffType,
      designation: member.designation,
      skill: member.skill,
      workTypes: Array.isArray(member.workTypes) && member.workTypes.length > 0 ? member.workTypes : [member.skill],
      teamName: member.teamName,
      supervisorId: member.supervisorId ? String(member.supervisorId) : '',
      availability: member.availability,
      joiningDate: member.joiningDate || '',
      profilePhoto: member.profilePhoto || '',
      notes: member.notes || '',
      isActive: member.isActive,
    });
    setShowAddModal(true);
  };

  const toggleSkillInForm = (catalogueSkill: SkillItem) => {
    setSelectedSkills((prev) => {
      const exists = prev.some((s) => s.skillId === catalogueSkill.id);
      if (exists) {
        const remaining = prev.filter((s) => s.skillId !== catalogueSkill.id);
        // Ensure at least one primary if remaining is non-empty
        if (remaining.length > 0 && !remaining.some((s) => s.isPrimary)) {
          remaining[0].isPrimary = true;
        }
        return remaining;
      } else {
        const isFirst = prev.length === 0;
        return [
          ...prev,
          {
            skillId: catalogueSkill.id,
            skillName: catalogueSkill.name,
            category: catalogueSkill.category,
            isPrimary: isFirst,
            proficiency: 'Intermediate',
            qualificationRef: '',
            notes: '',
            verificationStatus: 'UNVERIFIED',
          },
        ];
      }
    });
  };

  const setPrimarySkillInForm = (skillId: number) => {
    setSelectedSkills((prev) =>
      prev.map((s) => ({
        ...s,
        isPrimary: s.skillId === skillId,
      }))
    );
  };

  const updateSkillProficiencyInForm = (skillId: number, proficiency: SkillProficiency) => {
    setSelectedSkills((prev) =>
      prev.map((s) => (s.skillId === skillId ? { ...s, proficiency } : s))
    );
  };

  const updateSkillQualRefInForm = (skillId: number, qualificationRef: string) => {
    setSelectedSkills((prev) =>
      prev.map((s) => (s.skillId === skillId ? { ...s, qualificationRef } : s))
    );
  };

  const toggleCategoryExpand = (cat: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [cat]: !prev[cat],
    }));
  };

  const handleSubmitStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      const isEditing = Boolean(editingStaff);
      const url = isEditing ? `/api/staff/${editingStaff!.id}` : '/api/staff';
      const method = isEditing ? 'PUT' : 'POST';

      const payload = {
        ...form,
        skills: selectedSkills.map((sk) => ({
          skillId: sk.skillId,
          isPrimary: sk.isPrimary,
          proficiency: sk.proficiency,
          qualificationRef: sk.qualificationRef,
          notes: sk.notes,
        })),
        supervisorId: form.supervisorId ? Number(form.supervisorId) : null,
      };

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok) {
        if (!isEditing) {
          setRegistrationSuccessModal(data);
          setToastMessage(`✅ Successfully registered ${data.name} with Employee Code: ${data.employeeId}`);
        } else {
          setToastMessage(`Staff member ${data.name || form.name} updated successfully`);
        }
        setShowAddModal(false);
        setEditingStaff(null);
        await fetchData();
      } else {
        setToastMessage(data.error || 'Failed to save staff record');
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Submission error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setToastMessage(null), 5000);
    }
  };

  const handleToggleActive = async (member: StaffItem) => {
    try {
      const newActive = !member.isActive;
      const res = await fetch(`/api/staff/${member.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...member,
          isActive: newActive,
          availability: newActive ? 'AVAILABLE' : 'INACTIVE',
        }),
      });

      if (res.ok) {
        setToastMessage(`${member.name} marked as ${newActive ? 'Active' : 'Deactivated'}`);
        await fetchData();
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Status update error');
    }
  };

  const onSiteCount = staff.filter((s) => s.availability === 'ON_SITE').length;
  const availableCount = staff.filter((s) => s.availability === 'AVAILABLE').length;
  const supervisorCount = staff.filter((s) => s.staffType === 'SUPERVISOR' || s.staffType === 'FIELD_ENGINEER').length;

  const filteredStaff = staff.filter((s) => {
    const matchesAvail = availabilityFilter === 'ALL' || s.availability === availabilityFilter;
    const matchesType = staffTypeFilter === 'ALL' || s.staffType === staffTypeFilter;
    const matchesWorkType =
      workTypeFilter === 'ALL' ||
      (Array.isArray(s.workTypes) && s.workTypes.some((w) => w.toLowerCase().includes(workTypeFilter.toLowerCase()))) ||
      s.skill.toLowerCase().includes(workTypeFilter.toLowerCase());

    const matchesSearch =
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.employeeId.toLowerCase().includes(search.toLowerCase()) ||
      (s.cnic && s.cnic.toLowerCase().includes(search.toLowerCase())) ||
      (s.mobile && s.mobile.toLowerCase().includes(search.toLowerCase())) ||
      s.skill.toLowerCase().includes(search.toLowerCase()) ||
      s.teamName.toLowerCase().includes(search.toLowerCase());

    return matchesAvail && matchesType && matchesWorkType && matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Staff Master &amp; Field Operational Teams
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-900 border border-blue-200">
              Phase B Master Data
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Authoritative registry of Field Engineers, Supervisors, Technicians, Contractors &amp; Subcontractors with 9 HERE4U Work Type qualifications.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <button
            onClick={() => onNavigate('mobile-job')}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-amber-500 text-slate-950 hover:bg-amber-400 shadow-sm transition-colors"
          >
            <Smartphone className="w-3.5 h-3.5 text-slate-950" />
            Launch Mobile Job App
          </button>

          <button
            onClick={() => onNavigate('attendance')}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 shadow-sm transition-colors"
          >
            <MapPin className="w-3.5 h-3.5 text-blue-600" />
            Live GPS Attendance
          </button>

          <button
            onClick={openAddModal}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-lg bg-blue-700 text-white hover:bg-blue-800 shadow-sm transition-colors"
          >
            <Plus className="w-3.5 h-3.5 text-blue-200" />
            Register Staff Member
          </button>
        </div>
      </div>

      {toastMessage && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-medium text-emerald-900 flex items-center gap-2 animate-in fade-in">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-xs">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Staff Force</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{staff.length}</div>
          <div className="text-[11px] text-slate-400 mt-0.5">{staff.filter((s) => s.isActive).length} Active records</div>
        </div>

        <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-xs">
          <div className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">On Site (GPS Active)</div>
          <div className="text-2xl font-bold text-emerald-600 mt-1">{onSiteCount}</div>
          <div className="text-[11px] text-emerald-700 mt-0.5">Checked-in at UBL Branch</div>
        </div>

        <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-xs">
          <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider">Available for Dispatch</div>
          <div className="text-2xl font-bold text-blue-600 mt-1">{availableCount}</div>
          <div className="text-[11px] text-blue-700 mt-0.5">Ready for new Work Orders</div>
        </div>

        <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-xs">
          <div className="text-xs font-semibold text-purple-600 uppercase tracking-wider">Engineers &amp; Supervisors</div>
          <div className="text-2xl font-bold text-purple-600 mt-1">{supervisorCount}</div>
          <div className="text-[11px] text-purple-700 mt-0.5">Site Leads &amp; Verifiers</div>
        </div>
      </div>

      {/* Tab Switcher: Staff vs Teams */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
        <button
          onClick={() => setViewTab('STAFF')}
          className={`px-3.5 py-1.5 text-xs font-bold rounded-lg transition-colors ${
            viewTab === 'STAFF' ? 'bg-blue-700 text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          Staff Master Directory ({staff.length})
        </button>
        <button
          onClick={() => setViewTab('TEAMS')}
          className={`px-3.5 py-1.5 text-xs font-bold rounded-lg transition-colors ${
            viewTab === 'TEAMS' ? 'bg-blue-700 text-white shadow-xs' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          Operational Teams &amp; Hubs ({teams.length || 4})
        </button>
      </div>

      {viewTab === 'STAFF' ? (
        <div className="space-y-4">
          {/* Work Types Pill Bar (9 HERE4U WORK TYPES FILTER) */}
          <div className="p-3 bg-white border border-slate-200 rounded-xl shadow-xs space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5 text-blue-600" />
                Filter by 9 HERE4U Work Type Skills
              </span>
              <span className="text-[11px] text-slate-500">
                {workTypeFilter === 'ALL' ? 'Showing all skills' : `Filtered: ${workTypeFilter}`}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                onClick={() => setWorkTypeFilter('ALL')}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors ${
                  workTypeFilter === 'ALL'
                    ? 'bg-blue-700 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                All Skills
              </button>
              {HERE4U_WORK_TYPES.map((wt) => (
                <button
                  key={wt}
                  onClick={() => setWorkTypeFilter(wt)}
                  className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
                    workTypeFilter === wt
                      ? 'bg-blue-700 text-white font-semibold shadow-xs'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {wt}
                </button>
              ))}
            </div>
          </div>

          {/* Secondary Filters Bar */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden">
            <div className="p-3.5 border-b border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-3 bg-slate-50/70">
              <div className="flex flex-wrap items-center gap-2">
                {/* Staff Type Selector */}
                <div className="flex items-center gap-1 text-xs">
                  <span className="text-slate-500 font-medium">Type:</span>
                  <select
                    value={staffTypeFilter}
                    onChange={(e) => setStaffTypeFilter(e.target.value)}
                    className="px-2.5 py-1 text-xs bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 font-semibold"
                  >
                    <option value="ALL">All Staff Types</option>
                    <option value="FIELD_ENGINEER">FIELD_ENGINEER</option>
                    <option value="SUPERVISOR">SUPERVISOR</option>
                    <option value="TECHNICIAN">TECHNICIAN</option>
                    <option value="CONTRACTOR">CONTRACTOR</option>
                    <option value="SUBCONTRACTOR">SUBCONTRACTOR</option>
                    <option value="ESTIMATOR">ESTIMATOR</option>
                  </select>
                </div>

                {/* Availability Pills */}
                <div className="flex items-center gap-1 pl-2 border-l border-slate-200">
                  {(['ALL', 'AVAILABLE', 'ON_SITE', 'ASSIGNED', 'BUSY'] as const).map((mode) => (
                    <button
                      key={mode}
                      onClick={() => setAvailabilityFilter(mode)}
                      className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors ${
                        availabilityFilter === mode
                          ? 'bg-slate-900 text-white'
                          : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </div>

              {/* Search Box */}
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search name, code, CNIC, skill..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 w-full sm:w-64"
                />
              </div>
            </div>

            {/* Staff Directory Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-slate-600 font-semibold">
                    <th className="py-3 px-4">Employee</th>
                    <th className="py-3 px-4">Contact &amp; CNIC</th>
                    <th className="py-3 px-4">Staff Type &amp; Role</th>
                    <th className="py-3 px-4">Work Types / Skills</th>
                    <th className="py-3 px-4">Team &amp; Supervisor</th>
                    <th className="py-3 px-4">Availability</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {filteredStaff.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-slate-400">
                        No staff members found matching the selected filters.
                      </td>
                    </tr>
                  ) : (
                    filteredStaff.map((member) => (
                      <tr key={member.id} className="hover:bg-slate-50/80 transition-colors">
                        {/* Employee Avatar & Info */}
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs shrink-0 overflow-hidden border border-blue-200">
                              {member.profilePhoto ? (
                                <img src={member.profilePhoto} alt={member.name} className="w-full h-full object-cover" />
                              ) : (
                                member.name.charAt(0)
                              )}
                            </div>
                            <div>
                              <div className="font-bold text-slate-900 flex items-center gap-1.5">
                                {member.name}
                                {!member.isActive && (
                                  <span className="px-1.5 py-0.2 rounded text-[9px] bg-rose-100 text-rose-700 font-semibold">
                                    Inactive
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] font-mono text-slate-500">{member.employeeId}</div>
                            </div>
                          </div>
                        </td>

                        {/* Contact & CNIC */}
                        <td className="py-3 px-4">
                          <div className="text-[11px] text-slate-700 font-medium flex items-center gap-1">
                            <Phone className="w-3 h-3 text-slate-400" />
                            {member.mobile}
                          </div>
                          {member.cnic && (
                            <div className="text-[10px] text-slate-500 font-mono flex items-center gap-1 mt-0.5">
                              <IdCard className="w-3 h-3 text-slate-400" />
                              {member.cnic}
                            </div>
                          )}
                        </td>

                        {/* Staff Type & Designation */}
                        <td className="py-3 px-4">
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${
                              member.staffType === 'FIELD_ENGINEER'
                                ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                                : member.staffType === 'SUPERVISOR'
                                ? 'bg-purple-50 text-purple-700 border border-purple-200'
                                : member.staffType === 'CONTRACTOR'
                                ? 'bg-amber-50 text-amber-800 border border-amber-200'
                                : 'bg-slate-100 text-slate-700 border border-slate-200'
                            }`}
                          >
                            {member.staffType}
                          </span>
                          <div className="text-[11px] text-slate-600 font-medium mt-0.5">{member.designation}</div>
                        </td>

                        {/* Universal Skills & Verification Badges */}
                        <td className="py-3 px-4 max-w-sm">
                          <div className="flex flex-wrap gap-1 items-center">
                            {Array.isArray(member.assignedSkills) && member.assignedSkills.length > 0 ? (
                              member.assignedSkills.map((sk) => {
                                const isVerified = sk.verificationStatus === 'VERIFIED';
                                return (
                                  <span
                                    key={sk.id || sk.skillId}
                                    title={`${sk.skillName} (${sk.proficiency}) - ${sk.verificationStatus}${sk.qualificationRef ? ` - Ref: ${sk.qualificationRef}` : ''}`}
                                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                                      sk.isPrimary
                                        ? isVerified
                                          ? 'bg-emerald-50 text-emerald-800 border-emerald-300 font-bold'
                                          : 'bg-amber-50 text-amber-900 border-amber-300 font-bold'
                                        : isVerified
                                        ? 'bg-emerald-50/70 text-emerald-700 border-emerald-200'
                                        : 'bg-slate-50 text-slate-700 border-slate-200'
                                    }`}
                                  >
                                    {sk.isPrimary && <Star className="w-2.5 h-2.5 fill-amber-500 text-amber-600" />}
                                    <span>{sk.skillName}</span>
                                    <span
                                      className={`px-1 py-0.2 rounded text-[8px] font-bold ${
                                        isVerified
                                          ? 'bg-emerald-200 text-emerald-900'
                                          : 'bg-amber-100 text-amber-800'
                                      }`}
                                    >
                                      {isVerified ? 'VERIFIED' : 'UNVERIFIED'}
                                    </span>
                                  </span>
                                );
                              })
                            ) : (
                              (Array.isArray(member.workTypes) && member.workTypes.length > 0
                                ? member.workTypes
                                : [member.skill]
                              ).map((wt, i) => (
                                <span
                                  key={i}
                                  className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-800 border border-blue-200 whitespace-nowrap"
                                >
                                  {wt}
                                </span>
                              ))
                            )}
                          </div>
                        </td>

                        {/* Team & Supervisor */}
                        <td className="py-3 px-4">
                          <div className="font-semibold text-slate-800 text-[11px]">{member.teamName}</div>
                          {member.supervisorId && (
                            <div className="text-[10px] text-slate-500">
                              Lead:{' '}
                              {staff.find((s) => s.id === member.supervisorId)?.name || `ID #${member.supervisorId}`}
                            </div>
                          )}
                        </td>

                        {/* Availability Pill */}
                        <td className="py-3 px-4">
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-bold flex items-center gap-1 w-max ${
                              member.availability === 'ON_SITE'
                                ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                : member.availability === 'AVAILABLE'
                                ? 'bg-blue-50 text-blue-800 border border-blue-200'
                                : member.availability === 'ASSIGNED'
                                ? 'bg-amber-50 text-amber-800 border border-amber-200'
                                : 'bg-slate-100 text-slate-600'
                            }`}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${
                                member.availability === 'ON_SITE'
                                  ? 'bg-emerald-600 animate-pulse'
                                  : member.availability === 'AVAILABLE'
                                  ? 'bg-blue-600'
                                  : 'bg-amber-500'
                              }`}
                            />
                            {member.availability.replace('_', ' ')}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {/* Open Mobile View */}
                            <button
                              onClick={() => onNavigate('mobile-job', String(member.id))}
                              title="Open Mobile Job Field App as this Technician"
                              className="px-2 py-1 text-[11px] font-semibold rounded bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-200 flex items-center gap-1"
                            >
                              <Smartphone className="w-3 h-3 text-amber-700" />
                              Field App
                            </button>

                            <button
                              onClick={() => openEditModal(member)}
                              title="Edit Staff Master details"
                              className="p-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>

                            <button
                              onClick={() => handleToggleActive(member)}
                              title={member.isActive ? 'Deactivate' : 'Activate'}
                              className={`p-1 rounded ${
                                member.isActive
                                  ? 'bg-slate-100 hover:bg-rose-100 text-slate-600 hover:text-rose-700'
                                  : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700'
                              }`}
                            >
                              {member.isActive ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        /* TEAMS VIEW */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(teams.length > 0
            ? teams
            : [
                { teamName: 'South Rapid Response', lead: 'Farhan Zaidi', members: ['Farhan Zaidi', 'Muhammad Rizwan'], region: 'Sindh / Karachi' },
                { teamName: 'Team Alpha', lead: 'Engr. Bilal Hashmi', members: ['Sajid Mehmood', 'Noman Shah'], region: 'South Region' },
                { teamName: 'Team Beta', lead: 'Engr. Tariq Mehmood', members: ['Kamran Ali'], region: 'Civil & Architectural' },
                { teamName: 'Punjab Field Force', lead: 'Tariq Butt', members: ['Tariq Butt'], region: 'Islamabad / Lahore' },
              ]
          ).map((t, idx) => (
            <div key={idx} className="bg-white border border-slate-200 rounded-xl p-5 shadow-xs space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">{t.teamName}</h3>
                  <div className="text-xs text-slate-500 mt-0.5">{t.region || 'Active Maintenance Hub'}</div>
                </div>
                <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                  Active Team
                </span>
              </div>

              <div className="space-y-1.5 text-xs text-slate-700">
                <div>
                  <span className="text-slate-400 font-medium">Team Lead: </span>
                  <span className="font-semibold">{t.lead || 'Senior Site Supervisor'}</span>
                </div>
                <div>
                  <span className="text-slate-400 font-medium">Members: </span>
                  <span>{Array.isArray(t.members) ? t.members.join(', ') : 'Field Technicians'}</span>
                </div>
              </div>

              <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
                <span className="text-slate-400">Available for Dispatch</span>
                <button onClick={() => onNavigate('work-orders')} className="font-bold text-blue-700 hover:underline">
                  Create Work Order &rarr;
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit Staff Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-2xl my-8 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-700" />
                <div>
                  <h3 className="text-sm font-bold text-slate-900">
                    {editingStaff ? `Edit Staff Master: ${editingStaff.name}` : 'Register New Staff Member'}
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Phase B Staff Master Entity with 9 HERE4U Skills and Operational Classification.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-slate-400 hover:text-slate-600 text-xl font-bold leading-none p-1"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmitStaff} className="p-6 space-y-4 text-xs">
              {/* SECTION 1: Identity & Contact */}
              <div className="space-y-3">
                <h4 className="font-bold text-slate-800 text-[11px] uppercase tracking-wider text-blue-700">
                  1. Identification &amp; Contact
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 flex items-center justify-between">
                      <span>Employee Code *</span>
                      <span className="text-[10px] text-blue-700 font-semibold flex items-center gap-1">
                        <Lock className="w-3 h-3 text-blue-600" />
                        {editingStaff ? 'Read-Only' : 'Auto-Assigned'}
                      </span>
                    </label>
                    <input
                      type="text"
                      readOnly
                      disabled
                      value={editingStaff ? form.employeeId : 'Auto-assigned by server (EMP-XXXX)'}
                      className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg font-mono text-slate-600 cursor-not-allowed font-semibold focus:outline-none"
                    />
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      {editingStaff
                        ? 'Historical employee codes cannot be modified or reassigned.'
                        : 'Server allocates next unique EMP-XXXX code with concurrency protection.'}
                    </p>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-slate-700 font-semibold mb-1">Full Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Asif Raza"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">Mobile Number *</label>
                    <input
                      type="text"
                      required
                      placeholder="+92 300 1234567"
                      value={form.mobile}
                      onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">CNIC / ID Reference</label>
                    <input
                      type="text"
                      placeholder="42101-1234567-1"
                      value={form.cnic}
                      onChange={(e) => setForm({ ...form, cnic: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-mono focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">Email Address</label>
                    <input
                      type="email"
                      placeholder="technician@naeembuilder.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* SECTION 2: Staff Type & Designation */}
              <div className="pt-3 border-t border-slate-200 space-y-3">
                <h4 className="font-bold text-slate-800 text-[11px] uppercase tracking-wider text-blue-700">
                  2. Operational Classification &amp; Role
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">Staff Type *</label>
                    <select
                      value={form.staffType}
                      onChange={(e) => setForm({ ...form, staffType: e.target.value as StaffType })}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="FIELD_ENGINEER">FIELD_ENGINEER</option>
                      <option value="SUPERVISOR">SUPERVISOR</option>
                      <option value="TECHNICIAN">TECHNICIAN</option>
                      <option value="CONTRACTOR">CONTRACTOR</option>
                      <option value="SUBCONTRACTOR">SUBCONTRACTOR</option>
                      <option value="ESTIMATOR">ESTIMATOR</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">Designation Title *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Master Electrician / HVAC Lead"
                      value={form.designation}
                      onChange={(e) => setForm({ ...form, designation: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">Initial Availability</label>
                    <select
                      value={form.availability}
                      onChange={(e) => setForm({ ...form, availability: e.target.value as StaffAvailability })}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    >
                      <option value="AVAILABLE">AVAILABLE</option>
                      <option value="ON_SITE">ON_SITE</option>
                      <option value="ASSIGNED">ASSIGNED</option>
                      <option value="BUSY">BUSY</option>
                      <option value="OFF_DUTY">OFF_DUTY</option>
                      <option value="LEAVE">LEAVE</option>
                      <option value="INACTIVE">INACTIVE</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* SECTION 3: Universal Skills Catalogue & Assignment */}
              <div className="pt-3 border-t border-slate-200 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-slate-800 text-[11px] uppercase tracking-wider text-blue-700 flex items-center gap-1.5">
                    <Award className="w-3.5 h-3.5 text-blue-600" />
                    3. Universal Skills Catalogue &amp; Qualifications
                  </h4>
                  <span className="text-[11px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                    {selectedSkills.length} skills selected
                  </span>
                </div>

                {form.staffType === 'ESTIMATOR' && (
                  <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg text-[11px] text-blue-800 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0" />
                    <span>Office Staff Notice: Estimators and office roles do not require field trade skills. You may register without selecting trade skills.</span>
                  </div>
                )}

                {/* Skill Search Box */}
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search universal skill catalogue (e.g. VRF, Electrical, Painting, Tile, CCTV, Accounts)..."
                    value={skillsSearch}
                    onChange={(e) => setSkillsSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </div>

                {/* Categorized Skills Accordion */}
                <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-56 overflow-y-auto bg-slate-50/50">
                  {Array.from(new Set(skillsCatalogue.map((s) => s.category))).map((category) => {
                    const categorySkills = skillsCatalogue.filter(
                      (s) =>
                        s.category === category &&
                        (!skillsSearch ||
                          s.name.toLowerCase().includes(skillsSearch.toLowerCase()) ||
                          s.category.toLowerCase().includes(skillsSearch.toLowerCase()) ||
                          (s.description && s.description.toLowerCase().includes(skillsSearch.toLowerCase())))
                    );

                    if (categorySkills.length === 0) return null;
                    const isExpanded = expandedCategories[category] !== false;

                    return (
                      <div key={category} className="bg-white">
                        <button
                          type="button"
                          onClick={() => toggleCategoryExpand(category)}
                          className="w-full px-3 py-1.5 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                        >
                          <span className="font-bold text-[11px] text-slate-800 flex items-center gap-1.5">
                            {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                            {category}
                            <span className="text-[10px] text-slate-400 font-normal">({categorySkills.length})</span>
                          </span>
                          <span className="text-[10px] font-semibold text-slate-500">
                            {categorySkills.filter((cs) => selectedSkills.some((ss) => ss.skillId === cs.id)).length} selected
                          </span>
                        </button>

                        {isExpanded && (
                          <div className="p-2 grid grid-cols-2 sm:grid-cols-3 gap-1.5 bg-slate-50/60 border-t border-slate-100">
                            {categorySkills.map((sk) => {
                              const isSelected = selectedSkills.some((s) => s.skillId === sk.id);
                              return (
                                <button
                                  key={sk.id}
                                  type="button"
                                  onClick={() => toggleSkillInForm(sk)}
                                  className={`p-1.5 rounded-md text-left transition-all border flex items-center justify-between text-[11px] ${
                                    isSelected
                                      ? 'bg-blue-700 text-white border-blue-800 shadow-xs font-semibold'
                                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                                  }`}
                                >
                                  <span className="truncate pr-1">{sk.name}</span>
                                  {isSelected && <Check className="w-3 h-3 text-white shrink-0" />}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Selected Skills Detailed Assignment Table */}
                {selectedSkills.length > 0 && (
                  <div className="space-y-2 pt-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                        Configure Selected Skill Qualifications
                      </span>
                      <span className="text-[10px] text-slate-500">
                        Mark at most one skill as Primary
                      </span>
                    </div>

                    <div className="border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100 bg-white">
                      {selectedSkills.map((sk) => (
                        <div key={sk.skillId} className="p-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 hover:bg-slate-50/50">
                          <div className="flex items-center gap-2 sm:w-1/3">
                            <button
                              type="button"
                              onClick={() => setPrimarySkillInForm(sk.skillId)}
                              title={sk.isPrimary ? 'Primary skill' : 'Click to make Primary'}
                              className={`p-1 rounded-md border flex items-center gap-1 text-[10px] font-bold shrink-0 ${
                                sk.isPrimary
                                  ? 'bg-amber-100 border-amber-300 text-amber-900'
                                  : 'bg-slate-100 border-slate-200 text-slate-500 hover:bg-slate-200'
                              }`}
                            >
                              <Star className={`w-3 h-3 ${sk.isPrimary ? 'fill-amber-500 text-amber-600' : 'text-slate-400'}`} />
                              <span>{sk.isPrimary ? 'Primary' : 'Set Primary'}</span>
                            </button>
                            <div className="truncate">
                              <div className="font-bold text-slate-800 text-[11px] truncate">{sk.skillName}</div>
                              <div className="text-[10px] text-slate-400">{sk.category}</div>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-2 sm:w-2/3 justify-end">
                            {/* Proficiency Dropdown */}
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] text-slate-500">Proficiency:</span>
                              <select
                                value={sk.proficiency}
                                onChange={(e) => updateSkillProficiencyInForm(sk.skillId, e.target.value as SkillProficiency)}
                                className="px-2 py-1 text-[11px] font-semibold bg-white border border-slate-200 rounded-md focus:ring-1 focus:ring-blue-500"
                              >
                                <option value="Trainee">Trainee</option>
                                <option value="Basic">Basic</option>
                                <option value="Intermediate">Intermediate</option>
                                <option value="Advanced">Advanced</option>
                                <option value="Specialist">Specialist</option>
                              </select>
                            </div>

                            {/* Optional Qualification Ref */}
                            <input
                              type="text"
                              placeholder="Qual / License Ref (e.g. DAE-HVAC)"
                              value={sk.qualificationRef}
                              onChange={(e) => updateSkillQualRefInForm(sk.skillId, e.target.value)}
                              className="px-2 py-1 text-[11px] bg-slate-50 border border-slate-200 rounded-md w-36 focus:ring-1 focus:ring-blue-500"
                            />

                            {/* Verification Badge */}
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                              UNVERIFIED
                            </span>

                            {/* Remove Skill */}
                            <button
                              type="button"
                              onClick={() => toggleSkillInForm({ id: sk.skillId, name: sk.skillName, category: sk.category, skillCode: '', isActive: true })}
                              className="text-slate-400 hover:text-rose-600 p-1 font-bold text-sm"
                              title="Remove skill"
                            >
                              &times;
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-slate-400 italic">
                      * All newly registered qualifications start as UNVERIFIED. An authorized supervisor must audit and verify qualifications before they receive the official verified badge.
                    </p>
                  </div>
                )}
              </div>

              {/* SECTION 4: Team, Supervisor & Joining */}
              <div className="pt-3 border-t border-slate-200 space-y-3">
                <h4 className="font-bold text-slate-800 text-[11px] uppercase tracking-wider text-blue-700">
                  4. Team, Supervisor &amp; Profile
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">Operational Team</label>
                    <select
                      value={form.teamName}
                      onChange={(e) => setForm({ ...form, teamName: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none font-medium"
                    >
                      {Array.from(
                        new Set([
                          'Team Alpha',
                          'Team Beta',
                          'South Rapid Response',
                          'Punjab Field Force',
                          ...teams.map((t) => t.teamName),
                          ...(form.teamName ? [form.teamName] : []),
                        ].filter(Boolean))
                      ).map((tName) => (
                        <option key={tName} value={tName}>
                          {tName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">Supervisor / Lead</label>
                    <select
                      value={form.supervisorId}
                      onChange={(e) => setForm({ ...form, supervisorId: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none font-medium"
                    >
                      <option value="">No Supervisor (Independent)</option>
                      {staff
                        .filter(
                          (s) =>
                            (!editingStaff || s.id !== editingStaff.id) &&
                            (s.staffType === 'SUPERVISOR' ||
                              s.staffType === 'FIELD_ENGINEER' ||
                              (form.supervisorId && String(s.id) === String(form.supervisorId)))
                        )
                        .map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name} ({s.designation}) — {s.employeeId}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">Joining Date</label>
                    <input
                      type="date"
                      value={form.joiningDate}
                      onChange={(e) => setForm({ ...form, joiningDate: e.target.value })}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-slate-700 font-semibold mb-1">Specialization Notes &amp; Licenses</label>
                  <textarea
                    rows={2}
                    placeholder="e.g. Certified Daikin VRV technician, Grade-A electric wireman license, safety gear equipped."
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Form Action Buttons */}
              <div className="pt-4 border-t border-slate-200 flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer text-slate-700">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="font-semibold text-xs">Active Staff Member</span>
                </label>

                <div className="flex items-center gap-2.5">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || (form.staffType !== 'ESTIMATOR' && selectedSkills.length === 0)}
                    className="px-5 py-1.5 text-xs font-semibold rounded-lg bg-blue-700 hover:bg-blue-800 text-white transition-colors disabled:opacity-50"
                  >
                    {isSubmitting
                      ? 'Saving...'
                      : editingStaff
                      ? 'Update Staff Record'
                      : 'Register Staff Record'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Registration Success Confirmation Modal */}
      {registrationSuccessModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl border border-emerald-200 w-full max-w-md p-6 text-center animate-in zoom-in-95 duration-150">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto text-emerald-600 mb-3">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h3 className="text-base font-bold text-slate-900">Staff Registered Successfully!</h3>
            <p className="text-xs text-slate-500 mt-1">
              Automatic unique employee code allocated by ERP server:
            </p>

            <div className="my-4 p-3 bg-emerald-50 rounded-xl border border-emerald-200 font-mono text-xl font-bold text-emerald-900 tracking-wider">
              {registrationSuccessModal.employeeId}
            </div>

            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-left text-xs space-y-1 mb-4">
              <div className="flex justify-between">
                <span className="text-slate-500">Name:</span>
                <span className="font-semibold text-slate-900">{registrationSuccessModal.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Role / Type:</span>
                <span className="font-semibold text-slate-900">{registrationSuccessModal.staffType} ({registrationSuccessModal.designation})</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Primary Skill:</span>
                <span className="font-semibold text-slate-900">{registrationSuccessModal.skill}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Skills Assigned:</span>
                <span className="font-semibold text-blue-700">{registrationSuccessModal.assignedSkills?.length || 0} skills</span>
              </div>
            </div>

            <button
              onClick={() => setRegistrationSuccessModal(null)}
              className="w-full py-2 bg-blue-700 text-white rounded-lg font-semibold text-xs hover:bg-blue-800 transition-colors shadow-sm"
            >
              Continue to Staff Master
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
